/**
 * §C.1 · Restablecer la contraseña · `/restablecer?token=…`
 *
 * La dirección la fija el servidor: `PasswordReset` manda
 * `${APP_URL}/restablecer?token=…` en el correo.
 *
 * ## Diez caracteres y ninguna regla de composición
 *
 * Es lo que pide el contrato y la razón está escrita allí: las reglas de mayúsculas y
 * símbolos producen `Verano2024!` en todas partes, y **una frase larga es mejor
 * contraseña que un jeroglífico de ocho**. Así que aquí se pide longitud y se sugiere
 * una frase, en vez de un contador de requisitos que la gente satisface al mínimo.
 *
 * ## Cierra todas las sesiones, y hay que decirlo
 *
 * Quien restablece suele estar haciéndolo porque sospecha que otro entró. Dejarle viva
 * la sesión al otro convertiría el gesto en teatro, así que el servidor las cierra
 * todas — **incluida la del móvil de quien lo está haciendo**. Decirlo antes evita la
 * llamada de «me ha echado de la tablet del tatami».
 */
import { useState } from 'react';
import { api } from '@/shared/api/client';
import { aProblema, type Problema } from '@/shared/api/errores';
import { Error as PanelError } from '@/shared/ui/Estados';

const MINIMO = 10;

export function Restablecer({ token }: { token: string }) {
  const [contrasena, setContrasena] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);
  const [problema, setProblema] = useState<Problema | null>(null);

  const fijar = async () => {
    setEnviando(true);
    setProblema(null);

    const { error } = await api.v1.auth.password.reset.confirm.post({
      token,
      password: contrasena,
    });

    setEnviando(false);

    if (error) {
      setProblema(aProblema(error));
      return;
    }

    /*
     * El token fuera de la barra de direcciones, como en las otras tres pantallas
     * que reciben uno. Aquí es el único de los cuatro que se quedaba: ya está
     * gastado y el panel no carga nada de terceros, así que no hay por dónde
     * escaparse — pero un token en el historial y en la siguiente captura de
     * pantalla no se deja por costumbre. 22-09-2026.
     */
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    window.history.replaceState(null, '', url);

    setListo(true);
  };

  if (listo) {
    return (
      <main className="acceso">
        <div className="tarjeta">
          <h1>Contraseña cambiada</h1>
          <p>
            Ya puedes entrar con ella.{' '}
            <strong>Las demás sesiones se han cerrado</strong>: si tenías el
            panel abierto en otro sitio, habrá que entrar otra vez.
          </p>
          <button type="button" onClick={() => window.location.assign('/')}>
            Entrar
          </button>
        </div>
      </main>
    );
  }

  const corta = contrasena.length > 0 && contrasena.length < MINIMO;

  return (
    <main className="acceso">
      <div className="tarjeta">
        <h1>Una contraseña nueva</h1>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void fijar();
          }}
        >
          <label htmlFor="nueva">Contraseña</label>
          <input
            id="nueva"
            type="password"
            autoComplete="new-password"
            required
            minLength={MINIMO}
            value={contrasena}
            onChange={(e) => setContrasena(e.target.value)}
          />

          {/*
            Se sugiere una frase en vez de enumerar requisitos. Un contador de
            «una mayúscula, un número y un símbolo» se cumple al mínimo y produce
            la misma contraseña en todas partes.
          */}
          <p className="sub">
            Diez caracteres o más. Una frase que recuerdes —«el tatami huele a
            pino»— es mejor contraseña que ocho símbolos raros.
          </p>

          {corta && (
            <p className="sub">Te faltan {MINIMO - contrasena.length}.</p>
          )}

          <button
            type="submit"
            disabled={enviando || contrasena.length < MINIMO}
          >
            {enviando ? 'Guardando…' : 'Guardar y entrar'}
          </button>
        </form>

        <p className="sub">
          Al cambiarla <strong>se cierran todas tus sesiones</strong>, también
          las de otros dispositivos. Es lo que hace que sirva de algo si
          sospechas que alguien entró.
        </p>

        {problema && <PanelError problema={problema} />}
      </div>
    </main>
  );
}
